// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing — turning the engine's answers into plain words, rows and map lines.

import type { LatLon, RoutingDeparture, RoutingRoute, RoutingStatus } from '../../services/api';

export type Tone = 'ok' | 'warn' | 'err' | 'info' | 'mute';

const STATUS: Record<RoutingStatus, { tone: Tone; text: string }> = {
  VALID_WITHIN_SUPPLIED_DATA: { tone: 'ok', text: 'Route found' },
  NO_FEASIBLE_ROUTE_FOUND: { tone: 'warn', text: 'No route within the limits' },
  INSUFFICIENT_WEATHER_DATA: { tone: 'warn', text: 'Not enough forecast (it ends, or a field is missing)' },
  INSUFFICIENT_GEOGRAPHIC_DATA: { tone: 'warn', text: 'Not enough chart data along the way' },
  COMPUTATION_LIMIT_REACHED: { tone: 'err', text: 'Stopped at a search limit' },
};

export function statusView(s: RoutingStatus): { tone: Tone; text: string } {
  return STATUS[s] ?? { tone: 'mute', text: s };
}

/** "ECMWF IFS 0.25° 20260930T00Z", "gfs025" … → "ECMWF", "GFS", "ICON". */
export function modelName(idOrLabel: string): string {
  const s = idOrLabel.toLowerCase();
  if (s.includes('ecmwf')) return 'ECMWF';
  if (s.includes('gfs')) return 'GFS';
  if (s.includes('icon')) return 'ICON';
  return idOrLabel;
}

export function durationText(ms: number): string {
  const h = Math.round(ms / 3_600_000);
  return h < 48 ? `${h} h` : `${Math.floor(h / 24)} d ${h % 24} h`;
}

/** The route's points for a map line, with longitudes kept continuous across the date line. */
export function routeLine(route: RoutingRoute): LatLon[] {
  if (!route.legs.length) return [];
  const pts: LatLon[] = [route.legs[0].from, ...route.legs.map((l) => l.to)];
  const out: LatLon[] = [pts[0]];
  for (const [lat, lon] of pts.slice(1)) {
    let l = lon;
    const prev = out[out.length - 1][1];
    while (l - prev > 180) l -= 360;
    while (l - prev < -180) l += 360;
    out.push([lat, l]);
  }
  return out;
}

export interface WindowRow {
  departMs: number;
  /** The most robust route's worst-case passage time across the models, hours. */
  worstHours: number | null;
  /** Which model's route that is. */
  bestModel: string | null;
  /** Why there is none, per model ("GFS: forecast coverage"). */
  why: string;
}

/** One row per departure; `best` is the row with the shortest worst-case passage. */
export function windowRows(departures: RoutingDeparture[]): { rows: WindowRow[]; best: number | null } {
  const rows = departures.map((d): WindowRow => {
    const c = d.compromise == null ? null : d.candidates[d.compromise];
    return {
      departMs: d.departMs,
      worstHours: c?.worstArriveMs != null ? Math.round(((c.worstArriveMs - d.departMs) / 3_600_000) * 10) / 10 : null,
      bestModel: c ? modelName(c.fromForecast) : null,
      why: c ? '' : d.runs.filter((r) => r.cause !== 'none').map((r) => `${modelName(r.forecastId)}: ${r.cause}`).join(' · '),
    };
  });
  let best: number | null = null;
  rows.forEach((r, i) => {
    if (r.worstHours != null && (best == null || r.worstHours < (rows[best].worstHours as number))) best = i;
  });
  return { rows, best };
}
